import type { CommittedDelta, PerspectiveChangeRequest } from "@pax/world";
import type { Answered } from "./answered";
import { type JumpTurn, jumpTurn } from "./jump-turns";
import { type WaitingChat, waitingChats } from "./waiting-chats";
import { ownAlias } from "./world";
import { pendingPerspectiveChanges, reduceWorld, type WorldState } from "./world-state";

interface OwedBase {
  /** The work it belongs to. One piece of work under a key runs at a time. */
  key: string;
  /** One line saying what answering it means, for the game timeline. */
  label: string;
  /** The committed deltas it answers, oldest first. */
  deltas: CommittedDelta[];
}

/** One piece of work the game master owes, found in the record after every commit. */
export type OwedWork =
  | (OwedBase & { kind: "jump.batch"; turn: JumpTurn })
  | (OwedBase & { kind: "chat.reply"; chat: WaitingChat })
  | (OwedBase & { kind: "perspective_change"; request: PerspectiveChangeRequest });

/**
 * Everything the game master owes, in the order a one-at-a-time agent should answer it: the active
 * jump's next batch, every waiting chat, then every ask to change
 * perspective. While a jump is under way only asks to stop playing and watch are owed: taking up a
 * perspective waits for the jump to end, but anyone may step away at any moment.
 */
export function owedWork(
  history: readonly CommittedDelta[],
  state: WorldState,
  answered: Readonly<Answered>,
): { jump: JumpTurn | null; owed: OwedWork[] } {
  const jump = jumpTurn(history, answered.jump);
  const owed: OwedWork[] = [];
  if (jump?.owesBatch) {
    owed.push({
      kind: "jump.batch",
      key: jumpKey(jump),
      label: jump.turn === 1 ? "Open the jump" : `Jump batch ${jump.turn}`,
      deltas: jump.deltas,
      turn: jump,
    });
  }
  for (const chat of waitingChats(history, state, new Map(Object.entries(answered.chats)))) {
    owed.push({
      kind: "chat.reply",
      key: chat.key,
      label: chatLabel(state, chat),
      deltas: chat.deltas,
      chat,
    });
  }
  for (const request of pendingPerspectiveChanges(state)) {
    if (jump && request.to.type !== "spectator") continue;
    owed.push({
      kind: "perspective_change",
      key: `perspective-change:${request.id}`,
      label: `Answer ${perspectiveNamesOf(state, request.userId) || "a player"}'s ask to change perspective`,
      deltas: history.filter(delta => delta.revision === request.revision),
      request,
    });
  }
  return { jump, owed };
}

/** What the game master owes after `history`, as anyone holding only the record sees it. */
export function owedAfter(history: readonly CommittedDelta[]): OwedWork[] {
  return owedWork(history, reduceWorld(history), { jump: null, chats: {} }).owed;
}

const jumpKey = (turn: JumpTurn) => `jump:${turn.jump.round}`;

function chatLabel(state: WorldState, chat: WaitingChat): string {
  if (chat.type === "advisor") {
    return `Advise ${perspectiveNamesOf(state, chat.userId) || "a spectator"}`;
  }
  const view = state.perspectives[chat.perspectiveHandle];
  const thread = view?.threads.find(candidate => candidate.id === chat.threadId);
  return `Reply in ${view ? ownAlias(view).name : "a perspective"}'s chat "${thread?.title ?? chat.threadId}"`;
}

function perspectiveNamesOf(state: WorldState, userId: string): string {
  return Object.values(state.perspectives)
    .filter(view => view.userIds.includes(userId))
    .map(view => ownAlias(view).name)
    .join(" and ");
}
